/**
 * Media fallback resolution.
 *
 * Backend media is served from Cloudflare R2 via /api/media/*, but R2 is
 * currently disabled on the account, so those endpoints return 503 and
 * browsers refuse to render the JSON body in <img> tags (net::ERR_BLOCKED_BY_ORB).
 *
 * Known banners ship with the site under /public, so we resolve their keys to
 * bundled assets BEFORE making a doomed network request, and any other remote
 * media that still fails to load falls back to a bundled placeholder via
 * onError in <MediaImage />.
 */

/** Backend R2 media keys mapped to bundled local assets. */
const BUNDLED_MEDIA: Record<string, string> = {
    'banners/h-p.png': '/davidogbueli/h-p.3f232311c86d5634a079.png',
    'banners/h-l.png': '/davidogbueli/h-l.ed5d51393ba59b5cc150.png',
    'banners/h-p2.png': '/davidogbueli/h-p2.069e1f2176c57d2e6a05.png',
    'banners/h-l2.png': '/davidogbueli/h-l2.e0eb3d9a81ba788d2538.png',
    'banners/hn-p.png': '/davidogbueli/hn-p.4a4374dcfc57fb95568f.png',
    'banners/hn-l.png': '/davidogbueli/hn-l.d16bcb5d5c632895ddbb.png',
    'banners/P-2.png': '/davidogbueli/P-2.ac30fbcfafe114d57716.png',
    'banners/v-p.jpg': '/davidogbueli/v-p.1bf8b34c6cb4e722ec67.jpg',
    'banners/partner.jpg': '/davidogbueli/partner.c8845a089582c781856e.jpg',
    'banners/donate.png': '/davidogbueli/donate.89d5c648062868fc8650.png',
};

/** Generic bundled placeholder used when any remote media fails to load. */
export const DEFAULT_MEDIA_FALLBACK = '/backgrounds/hero-1.png';

/** Extract the R2 key from a media URL, e.g. 'banners/h-p2.png'. */
export function getMediaKey(url?: string | null): string | null {
    if (!url) return null;
    const marker = '/api/media/';
    const index = url.indexOf(marker);
    if (index === -1) return null;
    const key = url.slice(index + marker.length).split('?')[0].split('#')[0];
    return key ? decodeURIComponent(key) : null;
}

/** True when the URL points at the backend media endpoint. */
export function isBackendMediaUrl(url?: string | null): boolean {
    return getMediaKey(url) !== null;
}

/**
 * Resolve a media URL to a bundled local asset when its R2 key is known to
 * ship with the site. Returns null when there is no bundled copy — the caller
 * should then use the original URL with an onError fallback.
 */
export function resolveBundledMedia(url?: string | null): string | null {
    const key = getMediaKey(url);
    if (!key) return null;
    return BUNDLED_MEDIA[key] ?? null;
}
